import { useCallback, useEffect } from "react"

import { Controller, useForm } from "react-hook-form"

import styles from "./AccountingGroupForm.module.scss"
import BankCodeInput from "./BankCodeInput"
import BillingEmailsInput from "./BillingEmailsInput"
import BranchCodeInput from "./BranchCodeInput"

import { useBankLookup } from "@/company_admin/hooks/useBankLookup"
import {
	accountingGroupResolver,
	type AccountingGroupFormData,
} from "@/company_admin/utils/forms/accountingGroup"

type Props = {
	defaultValues?: Partial<AccountingGroupFormData>
	onSubmit: (data: AccountingGroupFormData) => Promise<void>
	onCancel: () => void
	submitLabel: string
	isSubmitting: boolean
}

const AccountingGroupForm = ({
	defaultValues,
	onSubmit,
	onCancel,
	submitLabel,
	isSubmitting,
}: Props) => {
	const {
		register,
		control,
		handleSubmit,
		watch,
		reset,
		formState: { errors },
	} = useForm<AccountingGroupFormData>({
		resolver: accountingGroupResolver,
		defaultValues: {
			name: "",
			displayName: "",
			bankCode: "",
			bankBranchCode: "",
			bankAccountType: "ORDINARY",
			bankAccountNumber: "",
			bankAccountHolderName: "",
			billingEmails: [],
			...defaultValues,
		},
	})

	useEffect(() => {
		if (defaultValues) {
			reset({
				name: "",
				displayName: "",
				bankCode: "",
				bankBranchCode: "",
				bankAccountType: "ORDINARY",
				bankAccountNumber: "",
				bankAccountHolderName: "",
				billingEmails: [],
				...defaultValues,
			})
		}
	}, [defaultValues, reset])

	const bankCode = watch("bankCode")
	const branchCode = watch("bankBranchCode")
	const { bankName, branchName } = useBankLookup(bankCode, branchCode)

	const onFormSubmit = useCallback(
		async (data: AccountingGroupFormData) => {
			await onSubmit(data)
		},
		[onSubmit]
	)

	return (
		<form
			className={styles.form}
			onSubmit={handleSubmit(onFormSubmit)}
			noValidate
		>
			<div className={styles.field}>
				<label className={styles.label}>グループ名</label>
				<input
					type="text"
					placeholder="グループ名を入力"
					className={`${styles.input} ${errors.name ? styles.input_error : ""}`}
					disabled={isSubmitting}
					{...register("name")}
				/>
				{errors.name && (
					<span className={styles.error_message}>{errors.name.message}</span>
				)}
			</div>

			<div className={styles.field}>
				<label className={styles.label}>表示名（明細・メールに表示）</label>
				<input
					type="text"
					placeholder="表示名を入力"
					className={`${styles.input} ${errors.displayName ? styles.input_error : ""}`}
					disabled={isSubmitting}
					{...register("displayName")}
				/>
				{errors.displayName && (
					<span className={styles.error_message}>
						{errors.displayName.message}
					</span>
				)}
			</div>

			<BankCodeInput
				register={register("bankCode")}
				bankName={bankName}
				error={errors.bankCode?.message}
			/>

			<BranchCodeInput
				register={register("bankBranchCode")}
				branchName={branchName}
				error={errors.bankBranchCode?.message}
			/>

			<div className={styles.field}>
				<label className={styles.label}>口座種別</label>
				<select
					className={`${styles.input} ${errors.bankAccountType ? styles.input_error : ""}`}
					disabled={isSubmitting}
					{...register("bankAccountType")}
				>
					<option value="ORDINARY">普通</option>
					<option value="CHECKING">当座</option>
				</select>
				{errors.bankAccountType && (
					<span className={styles.error_message}>
						{errors.bankAccountType.message}
					</span>
				)}
			</div>

			<div className={styles.field}>
				<label className={styles.label}>口座番号</label>
				<input
					type="text"
					placeholder="1234567"
					className={`${styles.input} ${errors.bankAccountNumber ? styles.input_error : ""}`}
					disabled={isSubmitting}
					{...register("bankAccountNumber")}
				/>
				{errors.bankAccountNumber && (
					<span className={styles.error_message}>
						{errors.bankAccountNumber.message}
					</span>
				)}
			</div>

			<div className={styles.field}>
				<label className={styles.label}>口座名義</label>
				<input
					type="text"
					placeholder="カ）ビューティーサロン"
					className={`${styles.input} ${errors.bankAccountHolderName ? styles.input_error : ""}`}
					disabled={isSubmitting}
					{...register("bankAccountHolderName")}
				/>
				{errors.bankAccountHolderName && (
					<span className={styles.error_message}>
						{errors.bankAccountHolderName.message}
					</span>
				)}
			</div>

			<Controller
				name="billingEmails"
				control={control}
				render={({ field }) => (
					<BillingEmailsInput
						emails={field.value ?? []}
						onChange={field.onChange}
						error={errors.billingEmails?.message}
					/>
				)}
			/>

			<div className={styles.buttons}>
				<button
					type="button"
					className={styles.cancel_button}
					onClick={onCancel}
					disabled={isSubmitting}
				>
					キャンセル
				</button>
				<button
					type="submit"
					className={styles.submit_button}
					disabled={isSubmitting}
				>
					{isSubmitting ? "保存中..." : submitLabel}
				</button>
			</div>
		</form>
	)
}

export default AccountingGroupForm
